import { useHistory } from 'react-router-dom';
import { useCallback } from 'react';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import useReduxPopupSystem from 'src/new_arch/hooks/useReduxPopupSystem';
import useCurrentUserOwnsWorkspace from 'src/new_arch/modules/workspaces/hooks/useCurrentUserOwnsWorkspace';
import Project from 'src/entities/project';
import ResponseError, { isResponseError } from 'src/new_arch/errors/ResponseError';
import { trackProjectMemberRoleEdited } from 'src/services/tracker/trackers/projects';

import ProjectMember from '../../models/projectMember';
import ProjectRole, { ProjectMemberRoleType } from '../../models/projectRole';
import useUpdateProjectMemberRoleMutation from '../../queries/useUpdateProjectMemberRoleMutation';

type UseUpdateProjectMemberRoleProps = {
	project: Project;
};

const isAForbiddenManagerRoleError = ( error: ResponseError ) => (
	error.status === 403 && ( error.data as Record<string, unknown> | undefined )?.error === 'forbidden_manager_role'
);

const useUpdateProjectMemberRole = ( { project }: UseUpdateProjectMemberRoleProps ) => {
	const snackbarSystem = useSnackbarSystem();
	const popupSystem = useReduxPopupSystem();
	const history = useHistory();

	const currentUserOwnsWorkspace = useCurrentUserOwnsWorkspace( { workspaceID: project.workspaceID } );

	const showOnlyWorkspaceMemmbersCanBeManagersPopup = useCallback( () => {
		if ( currentUserOwnsWorkspace ) {
			popupSystem.showDefaultConfirmationDialog( {
				title: 'Upgrade seat to manager',
				description: 'To upgrade their seat to manager you need to invite this person as a member in your workspace. You can do that in the members tab in your settings.',
				acceptText: 'Go to workspace settings',
				cancelText: 'Cancel',
				descriptionAlignment: 'center',
				acceptButtonColor: 'primary'
			} ).then( ( confirm: boolean ) => {
				popupSystem.hideConfirmationDialog();
				if ( confirm ) {
					history.push( `/user/workspaces/${project.workspaceID}/members` );
				}
			} );
		} else {
			popupSystem.showDialog( {
				title: 'Upgrade seat to manager',
				description: 'To upgrade their seat to manager you need to ask the workspace onwer to invite this person as a member in their workspace. They can do that in the members tab in settings.',
				acceptText: 'Close',
				bigTitle: false,
				descriptionAlignment: 'center',
				icon: 'info'
			} );
		}
	}, [ popupSystem, currentUserOwnsWorkspace, history, project.workspaceID ] );

	const { mutate: updateProjectMemberRole } = useUpdateProjectMemberRoleMutation( {
		projectID: project.id,
		onSuccess: ( member, role ) => {
			trackProjectMemberRoleEdited( { project, newRole: role } );

			snackbarSystem.showSuccessMessage( {
				title: 'Member role updated',
				description: `${member.user.name} has been updated to ${ProjectRole.fromID( role )?.name}`
			} );
		},
		onError: ( error ) => {
			if ( isResponseError( error ) && isAForbiddenManagerRoleError( error ) ) {
				showOnlyWorkspaceMemmbersCanBeManagersPopup();
				return;
			}

			snackbarSystem.showErrorMessage( { title: 'Error updating member role' } );
		}
	} );

	return useCallback( ( member: ProjectMember, role: ProjectMemberRoleType ) => {
		updateProjectMemberRole( { member, role } );
	}, [ updateProjectMemberRole ] );
};

export default useUpdateProjectMemberRole;
